import { useEffect, useRef, useState } from 'react';
import { siteConfig } from '../config/site';
import { useMoonbase } from '../lib/moonbase';
import { Icon } from './Icon';

export function MoonbaseSetupDialog() {
  const { setupOpen, closeSetup } = useMoonbase();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!setupOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeSetup();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('dialog-open');
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('dialog-open');
      previousFocus?.focus();
    };
  }, [closeSetup, setupOpen]);

  if (!setupOpen) return null;

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={closeSetup}>
      <section
        className="setup-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="moonbase-setup-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button ref={closeButtonRef} className="icon-button setup-dialog__close" type="button" onClick={closeSetup} aria-label="Close setup instructions">
          <Icon name="close" />
        </button>
        <span className="eyebrow">Store integration</span>
        <h2 id="moonbase-setup-title">Emberline storefront configuration</h2>
        <p>
          The public account URL and Emberline product ID are configured. Moonbase must allow the exact local and production storefront domains before embedded checkout can open.
        </p>
        <ol className="setup-steps">
          <li><span>1</span><div><strong>Moonbase account</strong><code>{siteConfig.moonbase.accountUrl}</code></div></li>
          <li><span>2</span><div><strong>Product ID</strong><code>wc-embr-mac-v1</code></div></li>
          <li><span>3</span><div><strong>Production domain</strong><code>wavecraft-audio.com</code></div></li>
          <li><span>4</span><div><strong>Development domain</strong><code>http://localhost:5173</code></div></li>
        </ol>
        <p className="fine-print">Full instructions are included in <code>docs/MOONBASE_SETUP.md</code>.</p>
        <button className="button button--dark" type="button" onClick={closeSetup}>Got it</button>
      </section>
    </div>
  );
}

export function MoonbaseSetupNotice() {
  const { configured, status, error, openSetup } = useMoonbase();
  const [dismissed, setDismissed] = useState(() => window.sessionStorage.getItem('wavecraft-hide-setup') === '1');

  if (configured && status === 'error') {
    return (
      <aside className="setup-notice setup-notice--error" aria-label="Storefront connection problem">
        <div>
          <span className="setup-notice__dot" />
          <strong>Storefront temporarily unavailable</strong>
          <span>{error ?? 'Moonbase could not be initialized.'}</span>
        </div>
        <div className="setup-notice__actions">
          <button type="button" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </aside>
    );
  }

  if (configured || dismissed || !siteConfig.moonbase.showSetupNotice) return null;

  const dismiss = () => {
    window.sessionStorage.setItem('wavecraft-hide-setup', '1');
    setDismissed(true);
  };

  return (
    <aside className="setup-notice" aria-label="Moonbase setup notice">
      <div>
        <span className="setup-notice__dot" />
        <strong>Store preview mode</strong>
        <span>Connect Moonbase to activate live prices, checkout, accounts and downloads.</span>
      </div>
      <div className="setup-notice__actions">
        <button type="button" onClick={openSetup}>Setup</button>
        <button type="button" onClick={dismiss} aria-label="Dismiss store setup notice"><Icon name="close" /></button>
      </div>
    </aside>
  );
}
